import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatErr } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import GlobalHeader from "../components/layout/GlobalHeader";
import PasswordInput from "./components/PasswordInput";
import { toast } from "sonner";
import { Wrench, Lock, ArrowRight, Sparkle, Scales, Fingerprint, Briefcase, HardHat } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Sprint H Batch 9.5 · Detectar producción para ocultar todo lo relacionado
  // con demo (bloque "PROBAR DEMO" + email/password pre-rellenados).
  const isProductionHost = typeof window !== "undefined"
    && /(^|\.)ag-ecosystem\.com$/i.test(window.location.hostname);

  const [email, setEmail] = useState(isProductionHost ? "" : "titular@demo.com");
  const [password, setPassword] = useState(isProductionHost ? "" : "demo1234");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      // Convergence Sprint Feb 2026: superadmin entra al Founder Control Center v2
      const dest = u?.role === "superadmin" ? "/founder" : "/";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const seedAndLogin = async (which) => {
    setBusy(true);
    try {
      const { data } = await api.post("/seed-demo");
      const creds = data[which];
      if (!creds) throw new Error(`Demo '${which}' no disponible`);
      setEmail(creds.email); setPassword(creds.password);
      const u = await login(creds.email, creds.password);
      const dest = u?.role === "superadmin" ? "/founder" : "/";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  // Sprint G real · #14 · Login con passkey
  const loginWithPasskey = async (opts = {}) => {
    const { discoverable = false } = opts;
    if (!discoverable && !email.trim()) { toast.error("Escribe primero tu email"); return; }
    if (!window.PublicKeyCredential) {
      if (!discoverable) toast.error("Tu navegador no soporta passkeys");
      return;
    }
    if (!discoverable) setBusy(true);
    try {
      const reqBody = discoverable ? {} : { email: email.trim() };
      const { data: serverOpts } = await api.post("/webauthn/login/options", reqBody);
      // Convertir base64url → bytes
      const b64urlToBytes = (b64url) => {
        const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
        const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
        const bin = atob(b64);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        return buf;
      };
      const bytesToB64url = (buf) => {
        const bytes = new Uint8Array(buf);
        let bin = "";
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      };
      const browserOpts = {
        ...serverOpts,
        challenge: b64urlToBytes(serverOpts.challenge),
        allowCredentials: (serverOpts.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBytes(c.id) })),
      };
      const getOpts = { publicKey: browserOpts };
      // Modo Conditional UI: el navegador escucha al focus del input email
      // y propone passkeys automáticamente (NO requiere pulsar botón).
      if (discoverable) getOpts.mediation = "conditional";
      const assertion = await navigator.credentials.get(getOpts);
      if (!assertion) return;
      const payload = {
        id: assertion.id, rawId: bytesToB64url(assertion.rawId), type: assertion.type,
        response: {
          authenticatorData: bytesToB64url(assertion.response.authenticatorData),
          clientDataJSON: bytesToB64url(assertion.response.clientDataJSON),
          signature: bytesToB64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bytesToB64url(assertion.response.userHandle) : null,
        },
        clientExtensionResults: assertion.getClientExtensionResults?.() || {},
      };
      await api.post("/webauthn/login/verify", { credential: payload });
      window.location.href = "/";
    } catch (err) {
      // En modo conditional NO mostramos errores (es silencioso por diseño)
      if (discoverable) return;
      const name = err?.name || "";
      if (name === "NotAllowedError") { toast.error("Has cancelado la autenticación biométrica"); }
      else if (name === "InvalidStateError") { toast.error("La passkey no es válida en este dispositivo"); }
      else if (name === "AbortError") { toast.error("Tiempo agotado. Vuelve a intentarlo."); }
      else {
        const backendDetail = formatErr(err?.response?.data?.detail);
        if (backendDetail && backendDetail !== "Algo salió mal. Inténtalo de nuevo.") toast.error(backendDetail);
        else toast.error(`Passkey · ${name}: ${(err?.message || "desconocido").slice(0, 200)}`);
      }
    } finally { if (!discoverable) setBusy(false); }
  };

  // Sprint G real · Conditional UI · Al montar, intenta auto-passkey si el navegador
  // soporta `isConditionalMediationAvailable`. Funciona en iOS 16+, Chrome Android 14+.
  useEffect(() => {
    if (!window.PublicKeyCredential) return;
    if (typeof PublicKeyCredential.isConditionalMediationAvailable !== "function") return;
    let aborted = false;
    PublicKeyCredential.isConditionalMediationAvailable().then((avail) => {
      if (avail && !aborted) loginWithPasskey({ discoverable: true });
    }).catch(() => {});
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col ag-radial">
      <GlobalHeader variant="auto" testId="login-header" showRegisterCta={false} />
      <div className="flex-1 grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="hidden lg:flex relative overflow-hidden ag-grid">
        <div className="relative z-10 flex flex-col justify-between p-8 lg:p-12 w-full">
          <Link to="/landing" className="flex items-center gap-2" data-testid="login-logo-link">
            <AutoGlowBrand size={28} />
          </Link>
          <div>
            <p className="font-display font-black text-5xl xl:text-6xl leading-[1.02] tracking-tighter">
              Tu negocio del motor,<br/>
              en <span className="ag-glow-text">piloto automático</span>.
            </p>
            <p className="text-sm text-[var(--ag-text-dim)] mt-6 max-w-md">
              Reservas online, leads cualificados, CRM, contabilidad IVA/IRPF y reparto titular/colaborador. Para talleres, detailing, chapa, neumáticos, ITV, autoescuelas y gestorías.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">
              <div><div className="text-[var(--ag-gold)] text-2xl font-display font-black tabular-nums">21%</div>IVA</div>
              <div><div className="text-[var(--ag-gold)] text-2xl font-display font-black tabular-nums">15%</div>IRPF</div>
              <div><div className="text-[var(--ag-gold)] text-2xl font-display font-black tabular-nums">30</div>días prueba</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-5 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--ag-gold)]">Acceso al sistema</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">Inicia sesión</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-3">Introduce tus credenciales o usa la demo.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Email</Label>
              <Input id="email" type="email" autoComplete="username webauthn" value={email} onChange={(e)=>setEmail(e.target.value)} required className="mt-1" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Contraseña</Label>
              <PasswordInput id="password" value={password} onChange={(e)=>setPassword(e.target.value)} required className="mt-1" testId="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full ag-btn-gold h-11" data-testid="login-submit">
              {busy ? "Conectando…" : <span className="flex items-center justify-center gap-2">Entrar <ArrowRight weight="bold" /></span>}
            </Button>
            <Button type="button" onClick={loginWithPasskey} disabled={busy || !email}
                    variant="outline"
                    className="w-full h-11 border-[var(--ag-gold)]/40 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/10 rounded-sm"
                    data-testid="login-passkey-btn">
              <Fingerprint weight="bold" className="mr-2" /> Iniciar sesión con passkey
            </Button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-[var(--ag-gold)] hover:underline" data-testid="forgot-link">
                ¿Has olvidado tu contraseña?
              </Link>
            </div>
          </form>

          {!isProductionHost && (
          <div className="mt-6 border border-dashed border-[var(--ag-border)] p-4 rounded bg-[var(--ag-midnight-soft)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--ag-gold)]">
              <Sparkle weight="fill" /> Probar demo
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              <Button type="button" variant="outline" onClick={()=>seedAndLogin("admin")} disabled={busy} className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight)] text-xs" data-testid="demo-admin-btn">
                <Lock size={12} className="mr-1" /> Titular
              </Button>
              <Button type="button" variant="outline" onClick={()=>seedAndLogin("colaborador")} disabled={busy} className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight)] text-xs" data-testid="demo-colab-btn">
                <Briefcase size={12} className="mr-1" /> Colab.
              </Button>
              <Button type="button" variant="outline" onClick={()=>seedAndLogin("gestor")} disabled={busy} className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight)] text-xs" data-testid="demo-gestor-btn">
                <Scales size={12} className="mr-1" /> Gestor
              </Button>
              <Button type="button" variant="outline" onClick={()=>seedAndLogin("empleado")} disabled={busy} className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight)] text-xs" data-testid="demo-empleado-btn">
                <HardHat size={12} className="mr-1" /> Empleado
              </Button>
            </div>
          </div>
          )}

          <p className="text-sm text-[var(--ag-text-dim)] mt-8">
            ¿No tienes cuenta? <Link to="/register" className="text-[var(--ag-gold)] font-medium underline" data-testid="goto-register">Crear centro</Link>
          </p>
          <div className="flex items-center justify-between gap-4 mt-3 text-xs text-[var(--ag-text-dim)]">
            <Link to="/pricing" className="hover:text-[var(--ag-gold)] underline-offset-4 hover:underline" data-testid="login-goto-pricing">
              Ver planes y precios →
            </Link>
            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem("ag_role_preference"); localStorage.removeItem("ag_splash_seen_v1"); } catch {}
                navigate("/welcome");
              }}
              data-testid="login-back-to-splash"
              className="hover:text-[var(--ag-gold)] underline-offset-4 hover:underline"
            >
              ← Volver al inicio
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
